<template>
  <base-resize-observer @resize="handleResize">
    <div data-component="base-tabs">
      <div
        ref="tabs"
        class="tabs"
      >
        <div class="tabs-left">
          <a
            v-for="({ value, label }) in visibleItems"
            :key="value"
            :data-tab="value"
            :class="{ active: activeTabId === value, 'active-only': onlyActiveItem }"
            @click.prevent="selectTab(value)"
          >
            <span class="label">
              {{ label }}
            </span>
          </a>
        </div>

        <or-icon-button
          v-if="menuItems.length"
          ref="button"
          data-menu-trigger
          icon="more_horiz"
          @click="isMenuOpen = true"
        />

        <base-tabs-mobile-popup
          v-if="isLayoutMobile && isMenuOpen"
          :menu-items="menuItems"
          :active-item-id="activeTabId"
          data-base-tabs-popup
          @item-click="selectTab($event)"
          @close-click="isMenuOpen = false"
        />

        <or-menu
          v-else-if="isMenuOpen"
          :trigger="buttonRoot"
          :is-open="isMenuOpen"
          data-base-tabs-menu
          @open="isMenuOpen = true"
          @close="isMenuOpen = false"
        >
          <or-menu-item
            v-for="({ value, label }) in menuItems"
            :key="value"
            :data-menu-item="value"
            :class="{ active: activeTabId === value }"
            @click.stop="selectTab(value)"
          >
            {{ label }}
          </or-menu-item>
        </or-menu>
      </div>

      <div class="content">
        <slot
          name="content"
          :item="activeItem"
        />
      </div>
    </div>
  </base-resize-observer>
</template>

<script lang="ts">
import _ from 'lodash';
import {
  defineComponent,
  PropType,
  toRefs,
  ref,
  computed,
  watch,
  getCurrentInstance,
  onMounted,
  onBeforeMount,
  WatchStopHandle,
} from 'vue-demi';
import {
  OrMenuV3 as OrMenu,
  OrMenuItemV3 as OrMenuItem,
  OrIconButtonV3 as OrIconButton,
  OrButtonV3 as OrButton,
} from '@onereach/ui-components-25';
import { useLayout } from '@/composables';
import { BaseResizeObserver } from '@/modules/virtual-scroll/components/decorators';
import BaseTabsMobilePopup from './BaseTabsMobilePopup.vue';
import { BaseTabItem } from '../../types';

export default defineComponent({
  name: 'BaseTabs',
  components: {
    OrMenu,
    OrMenuItem,
    OrIconButton,
    BaseResizeObserver,
    BaseTabsMobilePopup,
  },
  props: {
    items: {
      type: Array as PropType<BaseTabItem[]>,
      default: () => [],
    },
    activeItemId: {
      type: String,
      default: '',
    },
  },
  emits: ['tab-select'],
  setup(props) {
    const { items, activeItemId } = toRefs(props);
    const { isLayoutMobile } = useLayout();
    const vm = getCurrentInstance()!;
    const activeTabId = ref(activeItemId.value ?? items.value[0]?.value);
    const button = ref<InstanceType<typeof OrButton>>();
    const buttonRoot = computed(() => button.value?.root);

    const GAP = 24;
    const fitItemsCount = ref(items.value.length);
    const isMenuOpen = ref(false);
    const itemWidthList = ref([] as number[]);

    const activeItem = computed(() => {
      return items.value.find(item => item.value === activeTabId.value);
    });

    const activeItemIndex = computed(() => {
      return items.value.findIndex(item => item.value === activeTabId.value);
    });

    const visibleItems = computed(() => {
      const visibleList = items.value.slice(0, fitItemsCount.value);
      if (fitItemsCount.value > 0 && activeItemIndex.value > fitItemsCount.value - 1) {
        visibleList[visibleList.length - 1] = items.value[activeItemIndex.value];
      } else if (fitItemsCount.value === 0 && activeItemIndex.value >= 0) {
        visibleList[0] = items.value[activeItemIndex.value];
      }
      return visibleList;
    });

    const onlyActiveItem = computed(() => {
      return visibleItems.value.length === 1;
    });

    const menuItems = computed(() => {
      const menuList = items.value.slice(visibleItems.value.length);
      if (fitItemsCount.value > 0 && activeItemIndex.value > fitItemsCount.value - 1) {
        const activeItemMenuIndex = activeItemIndex.value - visibleItems.value.length;
        menuList[activeItemMenuIndex] = items.value[visibleItems.value.length - 1];
      }
      return menuList;
    });

    const childrenTotalWidth = computed(() => {
      const childrenWidthSum = itemWidthList.value.reduce((result, current) => result += current, 0);
      const gaps = itemWidthList.value.length > 1
        ? (itemWidthList.value.length - 1) * GAP
        : 0;
      return childrenWidthSum + gaps;
    });

    function reset() {
      fitItemsCount.value = items.value.length;
      isMenuOpen.value = false;
      itemWidthList.value = [];
    }

    function getTabsContainer() {
      return vm.proxy.$refs['tabs'] as HTMLElement;
    }

    function getChildrenList() {
      const tabsContainer = getTabsContainer();
      return Array.from(tabsContainer.firstElementChild!.children) as HTMLElement[];
    }

    function updateWidthList() {
      const childrenList = getChildrenList();
      itemWidthList.value = childrenList.map(child => child.offsetWidth);
    }

    function setTabs() {
      const tabsContainer = getTabsContainer();
      if (!tabsContainer) return;

      const containerPadding = GAP;
      const availableWidth = tabsContainer.offsetWidth - 2 * containerPadding;

      if (childrenTotalWidth.value <= availableWidth) {
        isMenuOpen.value = false;
        fitItemsCount.value = itemWidthList.value.length;
      } else {
        const MENU_BUTTON_SPACE = 20 + GAP;
        const itemsSpace = availableWidth - MENU_BUTTON_SPACE;
        let childrenWidthSum = childrenTotalWidth.value;
        let visibleItemsCount = 0;

        const widthList = _.clone(itemWidthList.value);
        const activeTabWidth = widthList.splice(activeItemIndex.value, 1);
        widthList.unshift(activeTabWidth[0]);

        for (let i = widthList.length - 1; i >= 0; i--) {
          childrenWidthSum -= (widthList[i] + (i > 0 ? GAP : 0));
          if (childrenWidthSum <= itemsSpace && childrenWidthSum > 0) {
            visibleItemsCount = i;
            break;
          }
        }

        fitItemsCount.value = visibleItemsCount;
      }
    }

    function selectTab(id: string) {
      if (id !== activeTabId.value) {
        isMenuOpen.value = false;
        activeTabId.value = id;
        vm.proxy.$emit('tab-select', id);
      }
    }

    let unwatchActiveItemId: WatchStopHandle = () => {};
    let unwatchItems: WatchStopHandle = () => {};
    onMounted(() => {
      unwatchActiveItemId = watch(activeItemId, value => {
        selectTab(value);
        vm.proxy.$nextTick(setTabs);
      });
      unwatchItems = watch(items, () => {
        reset();
        selectTab(activeItemId.value);
        vm.proxy.$nextTick(() => {
          updateWidthList();
          setTabs();
        });
      });
      updateWidthList();
    });
    onBeforeMount(() => {
      unwatchActiveItemId();
      unwatchItems();
    });

    const handleResize = _.debounce(() => {
      setTabs();
    });

    return {
      isLayoutMobile,
      activeItem,
      activeTabId,
      buttonRoot,
      isMenuOpen,
      button,
      visibleItems,
      onlyActiveItem,
      menuItems,
      handleResize,
      selectTab,
    };
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

[data-component='base-tabs'] {
  $root: &;

  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--or-c-background);

  .tabs {
    display: flex;
    gap: var(--s-5);
    justify-content: space-between;
    padding: var(--s-5) var(--s-4) 0;
    border-bottom: 1px solid var(--or-c-secondary);
    user-select: none;
    margin-bottom: var(--s-4);

    @include max-md {
      padding-top: var(--s-4);
    }
  }

  .tabs-left {
    display: flex;
    gap: var(--s-5);
    height: 1.8125rem;
    overflow: hidden;
  }

  [data-tab] {
    position: relative;
    display: flex;
    align-items: center;
    max-width: 12.25rem;
    padding-bottom: var(--s-3);
    font-weight: var(--fw-2);
    color: var(--or-c-secondary);
    cursor: pointer;

    &:hover {
      color: var(--or-c-on-background);
    }

    &.active {
      color: var(--or-c-on-background);

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--or-c-on-background);
      }

      &.active-only {
        min-width: 0;
      }
    }

    .label {
      @include text-overflow;

      font-size: var(--fs-1);

      &:not(:first-child) {
        margin-left: 0.5rem;
      }
    }
  }

  ::v-deep [data-menu-trigger] {
    background: none;
    outline: none!important;
  }

  .content {
    overflow: hidden;
    height: calc(100% - 4.375rem);
  }
}

[data-menu-item] {
    &.active {
      color: var(--or-c-on-background);
      font-weight: var(--fw-1);
      cursor: default;

      &:hover {
        background-color: unset!important;
      }
    }
  }
</style>
